import React from 'react';

// MIX A+D — Triangle icon (A) + typographic wordmark with bar (D)
// Variants exploring the combination

const LogoMix1 = ({ scale = 1 }) => (
  <div style={{ transform: `scale(${scale})`, transformOrigin: 'left center', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
    {/* Icon: Orange square + white triangle A with crossbar */}
    <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="46" height="46" rx="10" fill="#ea580c"/>
      {/* A legs */}
      <path d="M23 8 L37 36 H33 L29.5 28 H16.5 L13 36 H9 Z" fill="white"/>
      {/* Crossbar cutout to open the A */}
      <rect x="18" y="22" width="10" height="4" fill="#ea580c"/>
    </svg>
    {/* Wordmark: APEX bold + orange rule + RÉNOVATION light */}
    <div>
      <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: 22, lineHeight: 1, color: '#0f172a', letterSpacing: '1px' }}>APEX</div>
      <div style={{ width: '100%', height: 2, background: '#ea580c', margin: '4px 0 3px' }} />
      <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300, fontSize: 11, color: '#475569', letterSpacing: '3px', textTransform: 'uppercase' }}>Rénovation</div>
    </div>
  </div>
);

const LogoMix2 = ({ scale = 1 }) => (
  <div style={{ transform: `scale(${scale})`, transformOrigin: 'left center', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
    {/* Icon: Rounded square, A triangle but with a notch/peak at top */}
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="44" height="44" rx="11" fill="#0f172a"/>
      {/* Orange A triangle */}
      <path d="M22 7 L36 35 H30 L26.5 27 H17.5 L14 35 H8 Z" fill="#ea580c"/>
      {/* Crossbar cutout */}
      <rect x="18.5" y="21.5" width="9" height="3.5" fill="#0f172a"/>
    </svg>
    <div>
      <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: 20, lineHeight: 1, color: '#0f172a', letterSpacing: '2px' }}>APEX</div>
      <div style={{ width: '100%', height: 2, background: '#ea580c', margin: '4px 0 3px' }} />
      <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300, fontSize: 10.5, color: '#64748b', letterSpacing: '2.5px' }}>RÉNOVATION</div>
    </div>
  </div>
);

const LogoMix3 = ({ scale = 1 }) => (
  <div style={{ transform: `scale(${scale})`, transformOrigin: 'left center', display: 'inline-flex', alignItems: 'center', gap: 14 }}>
    {/* Icon: no background, just the A shape in orange */}
    <svg width="40" height="44" viewBox="0 0 40 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 2 L38 40 H31 L27 31 H13 L9 40 H2 Z" fill="#ea580c"/>
      <rect x="14.5" y="24" width="11" height="4.5" fill="white"/>
    </svg>
    <div>
      <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 21, lineHeight: 1, color: '#0f172a', letterSpacing: '0.5px' }}>
        Apex <span style={{ color: '#ea580c', fontWeight: 900 }}>—</span>
      </div>
      <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300, fontSize: 12, color: '#475569', letterSpacing: '3px', marginTop: 3 }}>RÉNOVATION</div>
    </div>
  </div>
);

const LogoMix4 = ({ scale = 1 }) => (
  <div style={{ transform: `scale(${scale})`, transformOrigin: 'left center', display: 'inline-flex', alignItems: 'center', gap: 11 }}>
    {/* Rounded square, white bg, orange A */}
    <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="46" height="46" rx="10" fill="#ea580c"/>
      {/* White filled A */}
      <path d="M23 8 L38 37 H32 L28.5 29 H17.5 L14 37 H8 Z" fill="white"/>
      {/* Orange crossbar cutout */}
      <rect x="19" y="23" width="10" height="4" fill="#ea580c"/>
      {/* Peak accent dot */}
      <circle cx="23" cy="6" r="2.5" fill="white" opacity="0"/>
    </svg>
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900, fontSize: 20, lineHeight: 1, color: '#0f172a', letterSpacing: '3px' }}>APEX</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
        <div style={{ flex: 1, height: 1.5, background: '#ea580c' }} />
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 9, color: '#ea580c', letterSpacing: '2px' }}>RÉNOVATION</div>
        <div style={{ flex: 1, height: 1.5, background: '#ea580c' }} />
      </div>
    </div>
  </div>
);

const variants = [
  { id: '1', label: 'Mix 1 — Carré orange + A blanc + barre', comp: LogoMix1 },
  { id: '2', label: 'Mix 2 — Carré ardoise + A orange + barre', comp: LogoMix2 },
  { id: '3', label: 'Mix 3 — A volant orange (sans fond) + tiret', comp: LogoMix3 },
  { id: '4', label: 'Mix 4 — Carré orange + règle bilatérale', comp: LogoMix4 },
];

export default function LogoPreview() {
  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '48px 60px', fontFamily: 'Inter, sans-serif' }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Logos Apex Rénovation — Mix A+D</h1>
      <p style={{ color: '#64748b', marginBottom: 40, fontSize: 14 }}>Triangle/sommet de l'option A · Wordmark typographique de l'option D</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
        {variants.map(({ id, label, comp: Comp }) => (
          <div key={id} style={{ background: 'white', borderRadius: 16, padding: '36px 40px', border: '1px solid #e2e8f0' }}>
            {/* Light bg */}
            <div style={{ marginBottom: 28 }}>
              <Comp scale={2} />
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid #f1f5f9', margin: '20px 0' }} />
            {/* Dark bg */}
            <div style={{ background: '#0f172a', borderRadius: 10, padding: '22px 28px', marginBottom: 20 }}>
              <Comp scale={1.7} />
            </div>
            {/* Small size test */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 40, marginBottom: 16 }}>
              <Comp scale={1} />
              <Comp scale={0.7} />
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 8 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
